import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { Check, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import StepBasics from "./wizard/StepBasics";
import StepSchema from "./wizard/StepSchema";
import { StepCalcModules, StepValidation, StepDrawings } from "./wizard/StepConfig";
import StepBom from "./wizard/StepBom";

const STEPS = ["Basic Info", "Spec Schema", "Calculation Modules", "Validation Domains", "Drawing Types", "BOM Template"];

const EMPTY_PACK = {
  name: "", description: "", icon: "puzzle", color: "var(--c-accent)", status: "draft", base_units: "metric",
  standards: [], sections: [], calc_modules: [], validation_domains: [], drawing_types: [], bom_template: [],
};

export default function PackWizard() {
  const { key: editKey } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [pack, setPack] = useState(EMPTY_PACK);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editKey) {
      api.get(`/industry-packs/${editKey}`)
        .then(({ data }) => setPack({ ...EMPTY_PACK, ...data }))
        .catch((e) => { toast.error(fmtErr(e)); nav("/industry-packs"); });
    }
  }, [editKey, nav]);

  const allFieldIds = useMemo(() => pack.sections.flatMap((s) => s.fields.map((f) => f.id)), [pack.sections]);

  const save = async () => {
    if (!pack.name.trim()) { toast.error("Pack name is required (Step 1)"); setStep(0); return; }
    setBusy(true);
    try {
      if (editKey) await api.put(`/industry-packs/${editKey}`, pack);
      else await api.post("/industry-packs", pack);
      toast.success("Industry pack saved");
      nav("/industry-packs");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-5xl" data-testid="pack-wizard">
      <button onClick={() => nav("/industry-packs")} data-testid="wizard-back"
        className="flex items-center gap-1.5 text-xs text-[var(--c-muted)] hover:text-[var(--c-text)] transition-colors duration-150 mb-4">
        <ArrowLeft size={13} /> Industry Packs
      </button>
      <h2 className="text-2xl tracking-tight font-medium text-[var(--c-text)]">{editKey ? `Edit · ${pack.name || editKey}` : "Create New Industry Pack"}</h2>

      <div className="flex items-center gap-0 mt-6 mb-8 overflow-x-auto" data-testid="wizard-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center shrink-0">
            <button type="button" onClick={() => setStep(i)} className="flex items-center gap-2" data-testid={`wizard-step-${i}`}>
              <div className={`w-6 h-6 rounded-sm flex items-center justify-center text-[11px] font-mono border ${
                i === step ? "bg-[var(--c-accent)] border-[var(--c-accent)] text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>{i + 1}</div>
              <span className={`text-xs whitespace-nowrap ${i === step ? "text-[var(--c-text)]" : "text-[var(--c-muted)]"}`}>{s}</span>
            </button>
            {i < STEPS.length - 1 && <div className="w-6 h-px bg-[var(--c-border)] mx-2.5" />}
          </div>
        ))}
      </div>

      {step === 0 && <StepBasics pack={pack} setPack={setPack} />}
      {step === 1 && <StepSchema pack={pack} setPack={setPack} />}
      {step === 2 && <StepCalcModules pack={pack} setPack={setPack} allFieldIds={allFieldIds} />}
      {step === 3 && <StepValidation pack={pack} setPack={setPack} />}
      {step === 4 && <StepDrawings pack={pack} setPack={setPack} />}
      {step === 5 && <StepBom pack={pack} setPack={setPack} />}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--c-border)] max-w-5xl">
        <Button data-testid="wizard-back-btn" variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)}
          className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <div className="text-[10px] font-mono tracking-widest text-[var(--c-muted)] uppercase">
          {pack.sections.reduce((a, s) => a + s.fields.length, 0)} fields · {pack.calc_modules.length} calc · {pack.validation_domains.length} domains · {pack.drawing_types.length} drawings
        </div>
        {step < 5 ? (
          <Button data-testid="wizard-next-btn" onClick={() => setStep((s) => s + 1)}
            className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
            Continue <ArrowRight size={14} className="ml-1.5" />
          </Button>
        ) : (
          <Button data-testid="wizard-save-btn" onClick={save} disabled={busy}
            className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm">
            {busy ? "Saving…" : "Save Pack"} <Check size={14} className="ml-1.5" weight="bold" />
          </Button>
        )}
      </div>
    </div>
  );
}
